<template>
  <div
    v-if="dialog && dialog.show"
    ref="dialogContainerRef"
    class="fixed inset-0 z-50 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="confirm-dialog-title"
    aria-describedby="confirm-dialog-description"
    @keydown="handleEscape"
  >
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
      <!-- Backdrop -->
      <div
        class="fixed inset-0 modal-backdrop transition-opacity animate-fade"
        aria-hidden="true"
        @click="dialog.onCancel?.()"
      />

      <!-- Dialog card -->
      <div
        class="relative bg-white rounded-2xl text-left overflow-hidden shadow-modal transform transition-all sm:max-w-lg sm:w-full border border-gray-200 animate-modal"
      >
        <div class="px-6 pt-6 pb-4">
          <div class="flex items-start">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-11 w-11 rounded-xl"
              :class="iconBgClasses"
            >
              <svg
                aria-hidden="true"
                class="h-5 w-5"
                :class="iconClasses"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div class="mt-0 ml-4 text-left flex-1">
              <h3 id="confirm-dialog-title" class="text-lg font-bold text-gray-900">
                {{ dialog.title }}
              </h3>
              <p
                id="confirm-dialog-description"
                class="mt-2 text-sm text-gray-500 leading-relaxed"
              >
                {{ dialog.message }}
              </p>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
          <button
            type="button"
            class="btn-press px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
            @click="dialog.onCancel?.()"
          >
            {{ dialog.cancelText ?? 'Cancel' }}
          </button>
          <button
            type="button"
            class="btn-press px-4 py-2.5 text-sm font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
            :class="dialog.confirmClass ?? 'bg-red-600'"
            @click="dialog.onConfirm?.()"
          >
            {{ dialog.confirmText ?? 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { nextTick } from 'vue';
import { useFocusTrap } from '@/composables/useFocusTrap';
import type { ConfirmDialog as ConfirmDialogState } from '@/stores/app';

interface Props {
  dialog: ConfirmDialogState | null;
  variant?: 'danger' | 'warning' | 'info';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'danger',
});

const focusTrap = useFocusTrap();
const dialogContainerRef = ref<HTMLElement | null>(null);

const iconClasses = computed(() => {
  const classes: Record<string, string> = {
    danger: 'text-red-600',
    warning: 'text-amber-600',
    info: 'text-blue-600',
  };
  return classes[props.variant] ?? classes.danger;
});

const iconBgClasses = computed(() => {
  const classes: Record<string, string> = {
    danger: 'bg-red-50',
    warning: 'bg-amber-50',
    info: 'bg-blue-50',
  };
  return classes[props.variant] ?? classes.danger;
});

watch(
  () => props.dialog?.show,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        const container = dialogContainerRef.value;
        if (container) {
          focusTrap.activate(container);
        }
      });
    } else {
      focusTrap.deactivate();
    }
  }
);

function handleEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.dialog) {
    props.dialog.onCancel?.();
  }
}
</script>
